import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Mic, MicOff, MonitorUp, PhoneOff, Video, VideoOff } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CarteAction } from "@/components/assistant/CarteAction";

const initiales = (n) => n.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
const duree = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const Tuile = ({ nom, video, partage }) => (
  <div className="relative aspect-video flex items-center justify-center overflow-hidden" style={{ background: "var(--ink)", borderRadius: 18 }} data-testid="visio-tile">
    {partage ? <p className="text-sm font-bold px-3 text-center" style={{ color: "var(--accent-ink)" }}><MonitorUp size={22} className="mx-auto mb-1" />Écran partagé</p>
      : <span className="w-14 h-14 at-pill flex items-center justify-center text-lg font-bold" style={{ background: video ? "var(--micro)" : "var(--muted)", color: "var(--ink)" }}>{initiales(nom)}</span>}
    <span className="absolute left-2 bottom-2 text-xs font-semibold px-2 py-0.5 at-pill" style={{ background: "var(--surface)" }}>{nom}</span>
  </div>
);

const Commande = ({ actif, onClick, label, testId, children, danger }) => (
  <button type="button" onClick={onClick} aria-label={label} title={label} data-testid={testId}
    className="w-14 h-14 at-pill flex items-center justify-center transition-transform duration-150 active:scale-95"
    style={{ background: danger ? "var(--danger)" : actif ? "var(--surface)" : "var(--muted)", color: danger ? "var(--accent-ink)" : "var(--ink)" }}>{children}</button>
);

const EnCours = ({ a, onMaj }) => {
  const { user } = useAuth();
  const [t, setT] = useState(0);
  const [micro, setMicro] = useState(true);
  const [cam, setCam] = useState(a.video);
  const [fin, setFin] = useState(false);
  useEffect(() => { const d = new Date(a.debut).getTime(); const i = setInterval(() => setT(Math.max(0, Math.floor((Date.now() - d) / 1000))), 1000); return () => clearInterval(i); }, [a.debut]);
  const partager = async () => { try { await api.post(`/visio/${a.id}/partage`, { actif: !a.partage_ecran }); onMaj(); } catch (e) { toast.error(messageErreur(e)); } };
  const raccrocher = async () => { setFin(true); try { await api.post(`/visio/${a.id}/terminer`); onMaj(); } catch (e) { toast.error(messageErreur(e)); setFin(false); } };
  const noms = [user.prenom ? `${user.prenom} ${user.nom}` : "Vous", ...a.participants_noms.filter((n) => n !== `${user.prenom} ${user.nom}`)];
  return (
    <div className="space-y-4" data-testid="visio-room">
      <p className="text-sm at-muted break-all" data-testid="visio-room-url">{a.salle} · {duree(t)}</p>
      <div className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-2">{noms.map((n, i) => <Tuile key={n} nom={n} video={i === 0 ? cam : a.video} partage={i === 0 && a.partage_ecran} />)}</div>
      {a.partage_ecran && <p className="text-xs font-semibold" data-testid="visio-sharing">Partage d'écran en cours ({a.partage_par}) · simulation</p>}
      <div className="flex justify-center gap-3 flex-wrap">
        <Commande actif={micro} onClick={() => setMicro(!micro)} label={micro ? "Couper le micro" : "Activer le micro"} testId="visio-toggle-mic">{micro ? <Mic size={20} /> : <MicOff size={20} />}</Commande>
        <Commande actif={cam} onClick={() => setCam(!cam)} label={cam ? "Couper la caméra" : "Activer la caméra"} testId="visio-toggle-cam">{cam ? <Video size={20} /> : <VideoOff size={20} />}</Commande>
        <Commande actif={a.partage_ecran} onClick={partager} label="Partager l'écran" testId="visio-toggle-share"><MonitorUp size={20} /></Commande>
        <Commande danger onClick={raccrocher} label="Raccrocher" testId="visio-hangup"><PhoneOff size={20} /></Commande>
      </div>
      {fin && <p className="text-sm text-center at-muted">L'IA retranscrit l'appel et prépare le plan d'actions…</p>}
    </div>
  );
};

const Termine = ({ a }) => (
  <div className="space-y-4" data-testid="visio-summary">
    <p className="text-sm at-muted">Appel terminé · {duree(a.duree_s || 0)} · {a.participants_noms.join(", ")}</p>
    <details className="at-card p-4" open data-testid="visio-transcript">
      <summary className="font-bold cursor-pointer min-h-[44px] flex items-center">Transcription (simulation)</summary>
      <p className="text-sm whitespace-pre-line break-words mt-2">{a.transcription}</p>
    </details>
    <section className="space-y-3" data-testid="visio-plan">
      <h2 className="text-base md:text-lg font-bold">Plan d'actions à valider ({a.plan?.length || 0})</h2>
      <p className="text-xs at-muted">Validez action par action. Ce qui n'a pas d'outil dans le CRM devient une tâche pour vous.</p>
      {(a.plan || []).map((p) => <CarteAction key={p.id} action={p} />)}
    </section>
  </div>
);

export default function SalleVisio() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const charger = useCallback(() => api.get(`/visio/${id}`).then((r) => setA(r.data)).catch((e) => toast.error(messageErreur(e))), [id]);
  useEffect(() => { charger(); }, [charger]);
  if (!a) return null;
  return (
    <div className="max-w-3xl space-y-4 min-w-0" data-testid="visio-page">
      <h1 className="text-2xl md:text-3xl font-bold">{a.video ? "Appel vidéo" : "Appel audio"}{a.fiche_nom ? ` · ${a.fiche_nom}` : ""}</h1>
      {a.fiche_id && <Link to={`/fiches/${a.fiche_id}`} className="text-sm font-semibold underline min-h-[44px] inline-flex items-center">Ouvrir la fiche</Link>}
      {a.statut === "en_cours" ? <EnCours a={a} onMaj={charger} /> : <Termine a={a} />}
    </div>
  );
}
